/**
 * Push Notification Service für iOS und Android
 * Verwendet @capacitor-firebase/messaging für FCM Token Support
 */

import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging, GetTokenOptions } from '@capacitor-firebase/messaging';
import { supabase } from '@/lib/supabase';
import { App } from '@capacitor/app';

export interface PushNotificationConfig {
  onNotificationReceived?: (notification: { title?: string; body?: string; data?: Record<string, unknown> }) => void;
  onNotificationActionPerformed?: (notification: { title?: string; body?: string; data?: Record<string, unknown> }) => void;
  onRegistrationError?: (error: unknown) => void;
}

export interface DeviceInfo {
  deviceId?: string;
  deviceModel?: string;
  osVersion?: string;
  appVersion?: string;
}

class PushNotificationService {
  private static instance: PushNotificationService;
  private initialized = false;
  private currentToken: string | null = null;
  private config: PushNotificationConfig = {};

  private constructor() {}

  static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService();
    }
    return PushNotificationService.instance;
  }

  /**
   * Prüft ob Push-Notifications auf dieser Plattform verfügbar sind
   */
  isAvailable(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Gibt die aktuelle Plattform zurück
   */
  getPlatform(): 'ios' | 'android' | 'web' {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios') return 'ios';
    if (platform === 'android') return 'android';
    return 'web';
  }

  /**
   * Initialisiert den Push-Notification Service
   */
  async initialize(config: PushNotificationConfig = {}): Promise<boolean> {
    if (!this.isAvailable()) {
      console.log('[Push] Not available on this platform');
      return false;
    }

    if (this.initialized) {
      console.log('[Push] Already initialized');
      return true;
    }

    this.config = config;

    try {
      // Listener registrieren
      await this.registerListeners();

      // Berechtigungen prüfen und anfordern
      const permStatus = await FirebaseMessaging.checkPermissions();
      console.log('[Push] Current permission status:', permStatus.receive);

      if (permStatus.receive === 'prompt') {
        const result = await FirebaseMessaging.requestPermissions();
        if (result.receive !== 'granted') {
          console.log('[Push] Permission denied');
          return false;
        }
      } else if (permStatus.receive !== 'granted') {
        console.log('[Push] Permission not granted:', permStatus.receive);
        return false;
      }

      // FCM Token abrufen
      const tokenOptions: GetTokenOptions = {};
      const { token } = await FirebaseMessaging.getToken(tokenOptions);

      if (token) {
        console.log('[Push] FCM Token received:', token.substring(0, 20) + '...');
        this.currentToken = token;
        await this.saveTokenToDatabase(token);
      }

      this.initialized = true;
      console.log('[Push] Initialized successfully');
      return true;
    } catch (error) {
      console.error('[Push] Initialization failed:', error);
      this.config.onRegistrationError?.(error);
      return false;
    }
  }

  /**
   * Registriert Event-Listener für Push-Notifications
   */
  private async registerListeners(): Promise<void> {
    // Token Refresh
    await FirebaseMessaging.addListener('tokenReceived', async ({ token }) => {
      console.log('[Push] Token refreshed:', token.substring(0, 20) + '...');
      this.currentToken = token;
      await this.saveTokenToDatabase(token);
    });

    // Notification im Vordergrund empfangen
    await FirebaseMessaging.addListener('notificationReceived', (event) => {
      console.log('[Push] Notification received:', event);
      this.config.onNotificationReceived?.({
        title: event.notification?.title,
        body: event.notification?.body,
        data: (event.notification?.data as Record<string, unknown>) ?? undefined,
      });
    });

    // Notification wurde angetippt (App im Hintergrund oder geschlossen)
    await FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
      console.log('[Push] Notification action performed:', event);
      this.config.onNotificationActionPerformed?.({
        title: event.notification?.title,
        body: event.notification?.body,
        data: (event.notification?.data as Record<string, unknown>) ?? undefined,
      });
    });
  }

  /**
   * Speichert den Push-Token in der Datenbank
   */
  private async saveTokenToDatabase(token: string): Promise<void> {
    try {
      const deviceInfo = await this.getDeviceInfo();
      const platform = this.getPlatform();

      const { data, error } = await supabase.rpc('register_push_token', {
        p_token: token,
        p_platform: platform,
        p_device_id: deviceInfo.deviceId || null,
        p_device_model: deviceInfo.deviceModel || null,
        p_os_version: deviceInfo.osVersion || null,
        p_app_version: deviceInfo.appVersion || null,
      });

      if (error) {
        console.error('[Push] Failed to save token:', error);
      } else {
        console.log('[Push] Token saved successfully:', data);
      }
    } catch (error) {
      console.error('[Push] Error saving token:', error);
    }
  }

  /**
   * Holt Geräteinformationen für die Token-Registrierung
   */
  private async getDeviceInfo(): Promise<DeviceInfo> {
    const info: DeviceInfo = {};

    try {
      const appInfo = await App.getInfo();
      info.appVersion = appInfo.version;
    } catch {
      // Ignorieren wenn nicht verfügbar
    }

    info.deviceModel = Capacitor.getPlatform();

    return info;
  }

  /**
   * Gibt den aktuellen Push-Token zurück
   */
  getToken(): string | null {
    return this.currentToken;
  }

  /**
   * Prüft ob Push-Notifications aktiviert sind
   */
  async checkPermissions(): Promise<'granted' | 'denied' | 'prompt'> {
    if (!this.isAvailable()) {
      return 'denied';
    }

    const result = await FirebaseMessaging.checkPermissions();
    if (result.receive === 'prompt-with-rationale') {
      return 'prompt';
    }
    return result.receive as 'granted' | 'denied' | 'prompt';
  }

  /**
   * Fordert Push-Berechtigungen an
   */
  async requestPermissions(): Promise<boolean> {
    if (!this.isAvailable()) {
      return false;
    }

    const result = await FirebaseMessaging.requestPermissions();
    return result.receive === 'granted';
  }

  /**
   * Deaktiviert Push-Notifications (z.B. bei Logout)
   */
  async deactivate(): Promise<void> {
    if (!this.currentToken) return;

    try {
      await supabase.rpc('deactivate_push_token', {
        p_token: this.currentToken,
      });
      console.log('[Push] Token deactivated');
    } catch (error) {
      console.error('[Push] Failed to deactivate token:', error);
    }
  }

  /**
   * Deaktiviert alle Push-Tokens des Users
   */
  async deactivateAll(): Promise<void> {
    try {
      await supabase.rpc('deactivate_all_push_tokens');
      console.log('[Push] All tokens deactivated');
    } catch (error) {
      console.error('[Push] Failed to deactivate all tokens:', error);
    }
  }

  /**
   * Löscht den FCM Token (z.B. bei Logout)
   */
  async deleteToken(): Promise<void> {
    try {
      await FirebaseMessaging.deleteToken();
      this.currentToken = null;
      console.log('[Push] Token deleted');
    } catch (error) {
      console.error('[Push] Failed to delete token:', error);
    }
  }

  /**
   * Entfernt alle Notification-Listeners und bereinigt den Service
   */
  async cleanup(): Promise<void> {
    await FirebaseMessaging.removeAllListeners();
    this.initialized = false;
    this.currentToken = null;
    console.log('[Push] Service cleaned up');
  }
}

// Singleton-Export
export const pushNotificationService = PushNotificationService.getInstance();

// Export der Klasse für Tests
export { PushNotificationService };
